// Deterministic visual derivations from string ids — backend doesn't carry a
// color/initials field yet, so we hash to a fixed palette so the same id
// always renders the same chip across sessions and clients.

import type { AvatarColor } from "@/lib/tokens";
import type { AvatarUser } from "@/components/ui/Avatar";
import type { FlowUser } from "@/lib/auth";

export const WORKSPACE_PALETTE = [
  "#00599B", // LogicLine Blau (primary)
  "#0D3E63", // Blau dunkel
  "#5B7A95", // Blau-Grau
  "#2F8FA6", // Petrol
  "#14807A", // Teal
  "#4C63C9", // Indigo
  "#7A5EA6", // Violett
  "#B45B7C", // Plum
  "#C74D6B", // Beere
  "#E5731A", // Akzent gedämpft
  "#C79A2E", // Senfgelb
  "#8B7355", // Warmes Braun
  "#3F8F65", // Gedämpftes Grün
  "#556B2F", // Olivgrün
  "#6B7280", // Graphit
  "var(--danger)", // Tief Rot
] as const;

export const WORKSPACE_PALETTE_LABELS: Record<(typeof WORKSPACE_PALETTE)[number], string> = {
  "#00599B": "LogicLine Blau",
  "#0D3E63": "Blau Dunkel",
  "#5B7A95": "Blau-Grau",
  "#2F8FA6": "Petrol",
  "#14807A": "Teal",
  "#4C63C9": "Indigo",
  "#7A5EA6": "Violett",
  "#B45B7C": "Plum",
  "#C74D6B": "Beere",
  "#E5731A": "Orange",
  "#C79A2E": "Senfgelb",
  "#8B7355": "Warmes Braun",
  "#3F8F65": "Grün",
  "#556B2F": "Olivgrün",
  "#6B7280": "Graphit",
  "var(--danger)": "Tief Rot",
};

const AVATAR_PALETTE: AvatarColor[] = ["violet", "blue", "amber", "green", "pink", "teal", "slate"];

function hashString(input: string): number {
  // Tiny djb2 — predictable, dependency-free.
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  return h >>> 0;
}

export function workspaceColor(workspaceId: string): string {
  return WORKSPACE_PALETTE[hashString(workspaceId) % WORKSPACE_PALETTE.length] as string;
}

/**
 * Resolve the display color for a workspace: explicit `workspace.color` wins,
 * otherwise the deterministic hash fallback. Use this anywhere you have the
 * Workspace object. Components that only have a workspace_id should call
 * useResolvedWorkspaceColor instead.
 */
export function resolveWorkspaceColor(
  workspace: { id: string; color?: string | null } | null | undefined,
  fallbackId?: string,
): string {
  if (workspace?.color) return workspace.color;
  const id = workspace?.id ?? fallbackId;
  return id ? workspaceColor(id) : (WORKSPACE_PALETTE[0] as string);
}

export function deriveInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return (first + last).toUpperCase();
}

export function deriveAvatarColor(seed: string): AvatarColor {
  return AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length] as AvatarColor;
}

// A unique, stable colour per identity. The old 7-slot AVATAR_PALETTE collided
// constantly; hashing the id onto the FULL OKLCH hue wheel makes two people
// practically never share a colour. One hue, three renderings from a single
// source: an avatar FILL (white text reads on it in both themes) and a text INK
// legible on the light / dark memo ground (for the Kürzel highlight). Pass the
// user id today; the seed is written as `user.color ?? user.id` at the call site
// so a future stored override is a one-line change, not a redo.
export function identityHue(seed: string): number {
  return hashString(seed) % 360;
}

export function identityFill(seed: string): string {
  return `oklch(62% 0.15 ${identityHue(seed)})`;
}

export function identityInk(seed: string): { light: string; dark: string } {
  const h = identityHue(seed);
  return { light: `oklch(46% 0.19 ${h})`, dark: `oklch(76% 0.14 ${h})` };
}

export function avatarUserFromFlowUser(u: FlowUser | null | undefined): AvatarUser | null {
  if (!u) return null;
  const label = u.name ?? u.email ?? u.sub;
  return {
    id: u.sub,
    display_name: label,
    initials: deriveInitials(u.name ?? u.email),
    avatar_color: deriveAvatarColor(u.sub),
    username: u.username ?? null,
  };
}
